import { DragEvent, useRef, useState } from "react";
import Papa from "papaparse";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroupContext from "@mui/material/ButtonGroup/ButtonGroupContext";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Modal from "@mui/material/Modal";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  HiOutlineCheckCircle,
  HiOutlineCloudUpload,
  HiOutlineDocumentText,
  HiOutlineDownload,
  HiOutlineExclamationCircle,
  HiOutlineX,
} from "react-icons/hi";
import { useNotify, useRefresh } from "react-admin";
import { colors, fontFamilyMono, radius, shadows } from "../../data/tokens";

// ------------------------------------------------------------------
// Importação de planilhas (CSV) — botão "Importar" de Cargos e Eleições.
// O arquivo vai para POST /uploads/csv/{tipo}, que o servidor processa
// DEPOIS de responder (por isso não há progresso do processamento, só do
// envio). A leitura no painel usa a mesma biblioteca e versão do servidor
// (papaparse 5.4.1), para a conferência das colunas bater com o que ele
// vai ler.
// ------------------------------------------------------------------

type ModelId = "position-roles" | "elections" | "users";

type ModelInfo = {
  label: string;
  description: string;
  template: string[]; // colunas do modelo de planilha, na ordem
  required: string[]; // colunas que o servidor lê de fato
  warning?: { severity: "warning" | "error"; text: string };
  disabledReason?: string;
  needsConfirmation?: string;
  // Linha que o servidor processaria errado (true = inválida) + explicação
  invalidRow?: { test: (row: Record<string, string>) => boolean; message: string };
};

const MODELS: Record<ModelId, ModelInfo> = {
  "position-roles": {
    label: "Cargos e Funções",
    description:
      "Liga cada sócio, pela matrícula, a um cargo e período. Linhas com matrícula não encontrada são ignoradas.",
    template: ["matricula", "nome", "cargo", "anuenio"],
    // O servidor usa matricula, cargo e anuenio; "nome" é só conferência
    required: ["matricula", "cargo", "anuenio"],
    // O servidor faz parseInt(row.matricula); quando dá NaN, a busca vira
    // "sem matrícula" e a linha é ligada a um sócio sem matrícula — o
    // errado (TODO.md, item 29). Mesma conta aqui, para barrar antes.
    invalidRow: {
      // eslint-disable-next-line radix
      test: (row) => Number.isNaN(parseInt(row.matricula)),
      message:
        "matrícula vazia ou que não é número — o servidor ligaria essas linhas a um sócio sem matrícula",
    },
  },
  elections: {
    label: "Eleições",
    description: "Participação de sócios em eleições.",
    template: ["nome", "ano", "participou", "cpf"],
    required: ["nome", "ano", "participou", "cpf"],
    // As colunas pedidas não existem no cadastro de eleições (name, year,
    // participated, userId) e o erro acontece depois da resposta, sem
    // tratamento — pode derrubar a API até o reinício automático
    // (TODO.md, item 29). Nunca funcionou; bloqueado até a correção.
    disabledReason:
      "Indisponível: hoje o servidor não converte estas colunas para o cadastro de eleições e a importação falha. Correção pendente no servidor.",
  },
  users: {
    label: "Membros (avançado)",
    description: "Grava as colunas do arquivo direto no cadastro de sócios.",
    template: [],
    required: [],
    warning: {
      severity: "error",
      text: "Uso técnico: as colunas vão direto para o banco, sem validação e sem cifrar senha. Use só com orientação do desenvolvedor.",
    },
    needsConfirmation:
      "Entendo que as colunas vão direto para o cadastro, sem validação",
  },
};

const MODEL_ORDER: ModelId[] = ["position-roles", "elections", "users"];

type Parsed = {
  fields: string[];
  rows: Record<string, string>[];
  hadBom: boolean;
  badEncoding: boolean;
};

const formatSize = (bytes: number) =>
  bytes < 1024
    ? `${bytes} B`
    : bytes < 1024 * 1024
    ? `${(bytes / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} KB`
    : `${(bytes / 1024 / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;

const hasUtf8Bom = (bytes: Uint8Array) =>
  bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf;

// Lê o arquivo como o servidor vai ler: texto UTF-8 e papaparse com
// cabeçalho. O BOM do "CSV UTF-8" do Excel é retirado — o servidor não o
// remove e não reconheceria a primeira coluna (ver uploadFile)
const readCsv = async (file: File): Promise<Parsed> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const hadBom = hasUtf8Bom(bytes);
  const text = new TextDecoder("utf-8").decode(hadBom ? bytes.subarray(3) : bytes);
  const result = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: true,
  });
  return {
    fields: result.meta.fields || [],
    rows: result.data,
    hadBom,
    // "�": bytes que não são UTF-8 (ex.: "CSV" comum do Excel, em
    // Windows-1252) — o servidor lê do mesmo jeito, com os acentos quebrados
    badEncoding: text.includes("�"),
  };
};

// Envio com progresso real (XHR: o fetch não informa o upload). Mesmo
// endereço, mesmo campo "file" e o token, como antes; só o BOM inicial é
// cortado, byte a byte, quando existir
const uploadFile = (
  file: File,
  model: ModelId,
  hadBom: boolean,
  onProgress: (percent: number) => void
) =>
  new Promise<{ status: number; body: any }>((resolve, reject) => {
    const toSend = hadBom
      ? new File([file.slice(3)], file.name, { type: file.type || "text/csv" })
      : file;
    const formData = new FormData();
    formData.append("file", toSend);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${process.env.REACT_APP_API_PATH}/uploads/csv/${model}`);
    const auth = localStorage.getItem("auth");
    const token = auth ? JSON.parse(auth)?.access_token : null;
    // A importação exige administrador logado (TODO.md, item 29)
    if (token) xhr.setRequestHeader("Authorization", "Bearer " + token);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      let body: any = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        // resposta sem corpo (o sucesso volta vazio)
      }
      resolve({ status: xhr.status, body });
    };
    xhr.onerror = () => reject(new Error("Falha de rede"));
    xhr.send(formData);
  });

const downloadTemplate = (model: ModelId) => {
  const info = MODELS[model];
  // Sem BOM: o servidor não o reconhece
  const blob = new Blob([info.template.join(",") + "\n"], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const slug = { "position-roles": "cargos", elections: "eleicoes", users: "membros" }[model];
  link.href = url;
  link.download = `modelo-importacao-${slug}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const sectionTitleSx = {
  fontSize: 11.5,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: colors.textMuted,
  marginBottom: 1,
};

type Status = "idle" | "reading" | "uploading" | "done" | "error";

const ImportButton = (props: any) => {
  const { defaultModel } = props;
  const initialModel: ModelId | null =
    defaultModel && (MODEL_ORDER as string[]).includes(defaultModel)
      ? (defaultModel as ModelId)
      : null;
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState<ModelId | null>(initialModel);
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const notify = useNotify();
  const refresh = useRefresh();

  const info = model ? MODELS[model] : null;
  const uploading = status === "uploading";
  const missing =
    info && parsed ? info.required.filter((c) => !parsed.fields.includes(c)) : [];
  const rowCount = parsed?.rows.length ?? 0;
  // Números das linhas no arquivo (a 1ª é o cabeçalho)
  const invalidLines =
    info?.invalidRow && parsed && missing.length === 0
      ? parsed.rows.reduce<number[]>(
          (lines, row, index) =>
            info.invalidRow!.test(row) ? [...lines, index + 2] : lines,
          []
        )
      : [];

  const blockReason = (() => {
    if (!info) return "Escolha o tipo de importação";
    if (info.disabledReason) return info.disabledReason;
    if (!file) return "Escolha o arquivo CSV";
    if (status === "reading") return "Lendo o arquivo…";
    if (!parsed) return "Não foi possível ler o arquivo";
    if (rowCount === 0) return "O arquivo não tem linhas de dados";
    if (missing.length > 0) return `Faltam colunas: ${missing.join(", ")}`;
    if (invalidLines.length > 0) return "Há linhas com matrícula inválida";
    if (info.needsConfirmation && !confirmed) return "Marque a confirmação";
    return "";
  })();

  const resetFile = () => {
    setFile(null);
    setParsed(null);
    setStatus("idle");
    setProgress(0);
    setErrorMessage("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleOpen = () => {
    setModel(initialModel);
    setConfirmed(false);
    resetFile();
    setOpen(true);
  };

  const handleClose = () => {
    if (uploading) return;
    setOpen(false);
  };

  const selectFile = async (selected: File | undefined | null) => {
    if (!selected) return;
    setFile(selected);
    setParsed(null);
    setErrorMessage("");
    setStatus("reading");
    try {
      setParsed(await readCsv(selected));
      setStatus("idle");
    } catch (error) {
      console.error(error);
      setStatus("error");
      setErrorMessage("Não foi possível ler o arquivo.");
    }
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragOver(false);
    if (!uploading) selectFile(event.dataTransfer.files?.[0]);
  };

  const handleImport = async () => {
    if (!model || !file || !parsed || blockReason) return;
    setStatus("uploading");
    setProgress(0);
    setErrorMessage("");
    try {
      const { status: httpStatus, body } = await uploadFile(
        file,
        model,
        parsed.hadBom,
        setProgress
      );
      if (httpStatus === 200 || httpStatus === 201) {
        setStatus("done");
        notify("Importação iniciada com sucesso. Aguarde.", { type: "success" });
      } else {
        const message = body && body.message ? body.message : "";
        setStatus("error");
        setErrorMessage(
          `O servidor recusou o arquivo${message ? `: ${message}` : ""} (HTTP ${httpStatus}).`
        );
        notify(`Erro ao importar arquivo. (${message}) - [${httpStatus}]`, {
          type: "error",
        });
      }
    } catch (error) {
      console.log("error", error);
      setStatus("error");
      setErrorMessage("Não foi possível enviar o arquivo. Confira a conexão e tente de novo.");
      notify("Erro ao importar arquivo.", { type: "error" });
    }
  };

  const previewFields = parsed?.fields ?? [];
  const previewRows = parsed?.rows.slice(0, 5) ?? [];
  const notCsvName = file && !/\.csv$/i.test(file.name);

  return (
    <>
      <Button
        color="primary"
        variant="outlined"
        sx={{ marginRight: 0.5 }}
        onClick={handleOpen}
        startIcon={<CloudUploadIcon />}
      >
        Importar
      </Button>

      {/* O botão Importar fica dentro do ButtonGroup do cabeçalho da
          página, que repassa variante/tamanho a todo botão descendente no
          React — inclusive aos do modal. Contexto neutro em volta do Modal
          (não dentro: o filho direto do Modal precisa aceitar ref, para o
          foco e o Esc funcionarem). */}
      <ButtonGroupContext.Provider value={{}}>
      <Modal
        aria-labelledby="import-modal-title"
        open={open}
        onClose={(_event, reason) => {
          // Clique fora não fecha (perderia o arquivo escolhido); Esc e
          // Cancelar fecham — nunca durante o envio
          if (reason === "backdropClick") return;
          handleClose();
        }}
        closeAfterTransition
      >
        <Fade in={open}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 2,
              pointerEvents: "none",
            }}
          >
            <Card
              sx={{
                pointerEvents: "auto",
                width: "min(880px, 96vw)",
                maxHeight: "88vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                borderRadius: `${radius.lg}px`,
                boxShadow: shadows.popover,
              }}
            >
              {/* Cabeçalho fixo: título e ações sempre visíveis */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1.5,
                  padding: "16px 24px",
                  borderBottom: `1px solid ${colors.border}`,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
                  <Typography
                    id="import-modal-title"
                    sx={{ fontSize: 18, fontWeight: 700, color: colors.textStrong }}
                  >
                    Importar planilha
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                    {info ? info.label : "Escolha o tipo"} · arquivo CSV
                  </Typography>
                </Box>
                {status === "done" ? (
                  <>
                    <Button
                      size="small"
                      onClick={() => {
                        refresh();
                        handleClose();
                      }}
                    >
                      Atualizar lista
                    </Button>
                    <Button variant="contained" onClick={handleClose}>
                      Fechar
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="small"
                      onClick={handleClose}
                      disabled={uploading}
                      sx={{ color: colors.textMuted }}
                    >
                      Cancelar
                    </Button>
                    <Tooltip title={uploading ? "" : blockReason}>
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleImport}
                          disabled={uploading || Boolean(blockReason)}
                          startIcon={
                            uploading ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <HiOutlineCloudUpload size={18} />
                            )
                          }
                        >
                          {uploading ? `Enviando… ${progress}%` : "Importar"}
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Box>

              {uploading && (
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ flexShrink: 0 }}
                />
              )}

              {/* Corpo com rolagem própria */}
              <Box sx={{ overflowY: "auto", padding: "20px 24px 24px" }}>
                {/* 1. Tipo */}
                <Typography sx={sectionTitleSx}>Tipo de importação</Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={model}
                  onChange={(_e, value) => {
                    if (value) {
                      setModel(value);
                      setConfirmed(false);
                      if (status === "error" || status === "done") {
                        setStatus("idle");
                        setErrorMessage("");
                      }
                    }
                  }}
                  disabled={uploading || status === "done"}
                  sx={{
                    flexWrap: "wrap",
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontSize: 13,
                      paddingX: 1.75,
                    },
                    "& .MuiToggleButton-root.Mui-selected": {
                      color: colors.primary,
                      backgroundColor: colors.bgContent,
                      fontWeight: 600,
                    },
                  }}
                >
                  {MODEL_ORDER.map((id) => (
                    <ToggleButton key={id} value={id}>
                      {MODELS[id].label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {info && (
                  <Box sx={{ marginTop: 1.5 }}>
                    <Typography sx={{ fontSize: 13.5, color: colors.textStrong }}>
                      {info.description}
                    </Typography>
                    {info.disabledReason && (
                      <Alert severity="warning" sx={{ marginTop: 1.5 }}>
                        {info.disabledReason}
                      </Alert>
                    )}
                    {info.warning && (
                      <Alert severity={info.warning.severity} sx={{ marginTop: 1.5 }}>
                        {info.warning.text}
                      </Alert>
                    )}
                  </Box>
                )}

                {/* 2. Colunas exigidas */}
                {info && !info.disabledReason && info.template.length > 0 && (
                  <Box sx={{ marginTop: 3 }}>
                    <Typography sx={sectionTitleSx}>Colunas da primeira linha</Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 0.75,
                      }}
                    >
                      {info.template.map((column) => {
                        const isRequired = info.required.includes(column);
                        const found = parsed ? parsed.fields.includes(column) : null;
                        return (
                          <Chip
                            key={column}
                            size="small"
                            variant="outlined"
                            color={
                              found === null
                                ? "default"
                                : found
                                ? "success"
                                : isRequired
                                ? "error"
                                : "default"
                            }
                            icon={
                              found === null ? undefined : found ? (
                                <HiOutlineCheckCircle size={15} />
                              ) : (
                                <HiOutlineExclamationCircle size={15} />
                              )
                            }
                            label={isRequired ? column : `${column} (opcional)`}
                            sx={{ fontFamily: fontFamilyMono }}
                          />
                        );
                      })}
                      <Button
                        size="small"
                        startIcon={<HiOutlineDownload size={16} />}
                        onClick={() => model && downloadTemplate(model)}
                        sx={{ marginLeft: "auto" }}
                      >
                        Baixar modelo
                      </Button>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: colors.textMuted, marginTop: 1 }}>
                      Nomes exatamente assim (minúsculas, sem acento). Separador
                      vírgula ou ponto e vírgula. No Excel, salve como "CSV UTF-8".
                    </Typography>
                  </Box>
                )}

                {/* 3. Arquivo (oculto quando o tipo está indisponível) */}
                <Box sx={{ marginTop: 3, display: info?.disabledReason ? "none" : "block" }}>
                  <Typography sx={sectionTitleSx}>Arquivo</Typography>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={(e) => selectFile(e.target.files?.[0])}
                  />
                  {!file ? (
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => inputRef.current?.click()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                      sx={{
                        border: `2px dashed ${dragOver ? colors.primary : colors.border}`,
                        backgroundColor: dragOver ? colors.bgContent : colors.bgSubtle,
                        borderRadius: `${radius.md}px`,
                        padding: { xs: 3, md: 4 },
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "border-color 0.2s ease, background-color 0.2s ease",
                        "&:hover": { borderColor: colors.primary },
                        "&:focus-visible": { outline: `2px solid ${colors.primary}` },
                      }}
                    >
                      <Box sx={{ color: colors.primary }}>
                        <HiOutlineCloudUpload size={32} />
                      </Box>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 600, color: colors.textStrong, marginTop: 1 }}
                      >
                        Arraste o arquivo CSV aqui ou clique para escolher
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: colors.textMuted, marginTop: 0.5 }}>
                        O arquivo é conferido antes do envio
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        border: `1px solid ${colors.border}`,
                        borderRadius: `${radius.md}px`,
                        padding: "12px 16px",
                      }}
                    >
                      <Box sx={{ color: colors.primary, flexShrink: 0 }}>
                        <HiOutlineDocumentText size={26} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{ fontSize: 14, fontWeight: 600, color: colors.textStrong }}
                        >
                          {file.name}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: colors.textMuted }}>
                          {formatSize(file.size)}
                          {status === "reading"
                            ? " · lendo…"
                            : parsed
                            ? ` · ${rowCount.toLocaleString("pt-BR")} ${rowCount === 1 ? "linha" : "linhas"} de dados · ${parsed.fields.length} colunas`
                            : ""}
                        </Typography>
                      </Box>
                      <Tooltip title="Trocar arquivo">
                        <span>
                          <IconButton
                            size="small"
                            onClick={resetFile}
                            disabled={uploading}
                            aria-label="Trocar arquivo"
                          >
                            <HiOutlineX size={18} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </Box>

                {/* 4. Conferência */}
                {parsed && info && (
                  <Box sx={{ marginTop: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                    {missing.length > 0 && (
                      <Alert severity="error">
                        Faltam as colunas <b>{missing.join(", ")}</b> na primeira linha
                        — sem elas o servidor não consegue importar. Confira os nomes
                        (ou baixe o modelo).
                      </Alert>
                    )}
                    {rowCount === 0 && (
                      <Alert severity="error">O arquivo não tem linhas de dados.</Alert>
                    )}
                    {invalidLines.length > 0 && info.invalidRow && (
                      <Alert severity="error">
                        {invalidLines.length === 1 ? "Linha" : "Linhas"}{" "}
                        <b>
                          {invalidLines.slice(0, 8).join(", ")}
                          {invalidLines.length > 8 ? ` e mais ${invalidLines.length - 8}` : ""}
                        </b>
                        : {info.invalidRow.message}. Corrija antes de importar.
                      </Alert>
                    )}
                    {parsed.hadBom && (
                      <Alert severity="info">
                        O arquivo começa com um caractere invisível (comum no "CSV
                        UTF-8" do Excel). Ele é retirado no envio, para o servidor
                        reconhecer a primeira coluna.
                      </Alert>
                    )}
                    {parsed.badEncoding && (
                      <Alert severity="warning">
                        Alguns caracteres aparecem como "�": o arquivo não está em
                        UTF-8 e os acentos vão chegar quebrados. No Excel, salve como
                        "CSV UTF-8".
                      </Alert>
                    )}
                    {notCsvName && (
                      <Alert severity="warning">
                        O arquivo não termina em .csv — confira se é mesmo uma
                        planilha CSV.
                      </Alert>
                    )}
                    {missing.length === 0 &&
                      rowCount > 0 &&
                      invalidLines.length === 0 &&
                      info.required.length > 0 && (
                      <Alert severity="success">
                        Colunas conferidas: {rowCount.toLocaleString("pt-BR")}{" "}
                        {rowCount === 1 ? "linha pronta" : "linhas prontas"} para importar.
                      </Alert>
                    )}
                  </Box>
                )}

                {/* 5. Prévia */}
                {parsed && previewFields.length > 0 && previewRows.length > 0 && (
                  <Box sx={{ marginTop: 2.5 }}>
                    <Typography sx={sectionTitleSx}>
                      Prévia · {previewRows.length} de {rowCount.toLocaleString("pt-BR")}{" "}
                      {rowCount === 1 ? "linha" : "linhas"}
                    </Typography>
                    <Box
                      sx={{
                        overflowX: "auto",
                        border: `1px solid ${colors.border}`,
                        borderRadius: `${radius.sm}px`,
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {previewFields.map((field) => (
                              <TableCell
                                key={field}
                                sx={{
                                  fontFamily: fontFamilyMono,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                  backgroundColor: colors.bgSubtle,
                                  color: info?.required.includes(field)
                                    ? colors.primaryDark
                                    : colors.textMuted,
                                }}
                              >
                                {field}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewRows.map((row, index) => (
                            <TableRow key={index}>
                              {previewFields.map((field) => (
                                <TableCell
                                  key={field}
                                  sx={{ fontSize: 12.5, whiteSpace: "nowrap", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}
                                >
                                  {row[field] ?? ""}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                )}

                {/* Confirmação do modo técnico */}
                {info?.needsConfirmation && file && (
                  <FormControlLabel
                    sx={{ marginTop: 2 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                        disabled={uploading || status === "done"}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13.5 }}>{info.needsConfirmation}</Typography>
                    }
                  />
                )}

                {/* 6. Resultado */}
                {status === "done" && (
                  <Alert severity="success" sx={{ marginTop: 2.5 }}>
                    Arquivo enviado: {rowCount.toLocaleString("pt-BR")}{" "}
                    {rowCount === 1 ? "linha" : "linhas"}. A importação continua no
                    servidor — os registros aparecem na lista em instantes (use
                    "Atualizar lista").
                  </Alert>
                )}
                {status === "error" && errorMessage && (
                  <Alert severity="error" sx={{ marginTop: 2.5 }}>
                    {errorMessage}
                  </Alert>
                )}
              </Box>
            </Card>
          </Box>
        </Fade>
      </Modal>
      </ButtonGroupContext.Provider>
    </>
  );
};

export default ImportButton;
